import {z} from 'zod'

export const OfferPatchSchema = z.object({
  id: z.number(),
  password: z.string(),
  currency: z.string(),
  timezone: z.string(),
})

export const OfferRequestSchema = z.object({
  id: z.string(),
  password: z.string(),
  internal: z.string().optional(),
})

export const OfferPostSchema = z.object({
  id: z.number(),
  password: z.string(),
})

export const CurrenciesRequestSchema = z.object({
  id: z.string(),
  password: z.string(),
  internal: z.string().optional(),
})

export const PdfRequestSchema = z.object({
  id: z.number(),
  password: z.string(),
  lang: z.string(),
})

export const TrackRequestSchema = z.object({
  email: z.string(),
  pid: z.number(),
  price: z.number(),
  id: z.number(),
})

const CustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  idReferrer: z.number().nullable(),
  currency: z.string(),
  contactTel: z.string().nullable(),
})

const ProjectSchema = z.object({
  id: z.number(),
  name: z.string(),
  quoteCurrency: z.string().nullable(),
  customerTimezone: z.number(),
})

const OfferSchema = z.object({
  id: z.number(),
  offerStatId: z.number(),
  createDate: z.string().nullable(),
  pass: z.string(),
  accepted: z.boolean(),
  deliveryDays: z.number(),
  expireAt: z.string().nullable(),
  acceptingIp: z.string().nullable(),
  acceptingDateTime: z.string().nullable(),
  fileList: z.array(z.string()),
  lastVisitDateTime: z.string().nullable(),
  customTerms: z.string().nullable(),
  notes: z.string().nullable(),
  viewedAfterAccepted: z.boolean().nullable(),
  inLavorazioneOnAccepted: z.boolean().nullable(),
})

const OfferStatSchema = z.object({
  id: z.number(),
  currentOffer: z.number().nullable(),
  lang: z.string(),
  notarisation: z.boolean(),
  requesterEmails: z.array(z.string()),
  requesterName: z.string().nullable(),
  requesterTel: z.string().nullable(),
})

const OfferTrackSchema = z.object({
  hash: z.string(),
})

const AmSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  title: z.string().nullable(),
  website: z.string().nullable(),
  mainPhone: z.string().nullable(),
  directPhone: z.string().nullable(),
  fax: z.string().nullable(),
  email: z.string().nullable(),
})

export const JobSchema = z.object({
  id: z.number(),
  projectId: z.number(),
  jobType: z.string(),
  subType: z.string().nullable(),
  source: z.string(),
  target: z.string(),
  customerWords: z.number(),
  translatorWords: z.number(),
  orderDate: z.string().nullable(),
  customerDeliveryDate: z.string().nullable(),
  customerTotal: z.number(),
  convertedCustomerTotal: z.number(),
  unitType: z.string(),
})
const JobsSchema = z.array(JobSchema)

export const RequestUnitType = z.enum([
  'char',
  'word',
  'page',
  'minute',
  'hour',
  'item',
])
export type RequestUnitType = z.infer<typeof RequestUnitType>

export const RequestSchema = z.object({
  id: z.number().int().positive(),
  idContent: z.string().min(1),
  requestType: z.enum(['operational', 'financial']),
  customerTotal: z.number(),
  convertedCustomerTotal: z.number(),
  contentFilename: z.string().nullish(),
  source: z.string().nullish(),
  target: z.string().nullish(),
  jobType: z.string().nullish(),
  quantity: z.number().nullish(),
  fileWcRaw: z.number().nullish(),
  unitType: RequestUnitType.nullish(),
  unitPrice: z.number().nullish(),
  convertedUnitPrice: z.number().nullish(),
  serviceType: z.string().nullish(),
})
export type RequestSchema = z.infer<typeof RequestSchema>
const RequestsSchema = z.array(RequestSchema)

const ComputedDataSchema = z.object({
  ip: z.string(),
  projectServiceType: z.string(),
  offerTotal: z.number(),
  offerConvertedTotal: z.number(),
  hasOfferExpired: z.boolean(),
  showOfferConversion: z.boolean(),
  hasOfferFixedDate: z.boolean().nullable().optional(),
  offerDeliveryDate: z.string().nullable(),
  offerExpireAt: z.string().nullable(),
})

export const OfferResponseSchema = z.object({
  customer: CustomerSchema,
  project: ProjectSchema,
  offer: OfferSchema,
  offerStat: OfferStatSchema,
  offerTrack: OfferTrackSchema,
  am: AmSchema,
  jobs: JobsSchema,
  computedData: ComputedDataSchema,
  requests: RequestsSchema.nullish(),
})
export type OfferResponseSchema = z.infer<typeof OfferResponseSchema>

export const CurrenciesResponseSchema = z.array(
  z.object({
    currency: z.string(),
    rate: z.number(),
  }),
)
export type CurrenciesResponseSchema = z.infer<typeof CurrenciesResponseSchema>

export const JobTypesResponseSchema = z.object({
  ASSEVERAZIONE: z.string(),
  BOLLI: z.string(),
  CERTIFICATION: z.string(),
  COPYREVIEW: z.string(),
  ['CREDIT NOTE']: z.string(),
  DISCOUNT: z.string(),
  DTP: z.string(),
  EMBEDDING: z.string(),
  POSTEDITING: z.string(),
  REVISIONE: z.string(),
  ['SOUND-EDITING']: z.string(),
  SPEDIZIONE: z.string(),
  SUBTITLING: z.string(),
  TRADUZIONE: z.string(),
  TRANSCR: z.string(),
  TRANSCREATION: z.string(),
  ['video-bundle']: z.string(),
  ['VIDEO-EDITING']: z.string(),
  VOICEOVER: z.string(),
  SUBTITLING_REVISION: z.string(),
  VOICEOVER_REVISION: z.string(),
  TRANSCREATION_REVISION: z.string(),
  FINANZIARIO: z.string(),
  FORNITORE: z.string(),
  ['mymemory-plugin-pro']: z.string(),
  ['SPESE BANC.']: z.string(),
  ['STAMP DUTY']: z.string(),
  UNKNOWN: z.string(),
  VALUTAZIONE: z.string(),
  ['AI-LABELING']: z.string(),
  QA: z.string(),
  ['SPECIAL DISCOUNT']: z.string(),
  ['QUALITY DISCOUNT']: z.string(),
  ['DELAY DISCOUNT']: z.string(),
  ['GOODWILL DISCOUNT']: z.string(),
  DUBBING: z.string(),
  LQA: z.string(),
  ['SWORN_TRANSLATION']: z.string(),
  ['AUDIO_TRANSCRPT']: z.string(),
  AUDIO_ANNOTATION: z.string(),
  AUDIO_ANNOTATION_REVISION: z.string(),
  ['BUG-FIXING']: z.string(),
  ['CLEAN-UP']: z.string(),
  CONSULTANCY: z.string(),
  EXTRA_SERVICES: z.string(),
  LL_SECOND_PASS: z.string(),
  LSO: z.string(),
  MACHINE_TRANSLATION: z.string(),
  ['MACHINE_TRANSLATION~lara']: z.string(),
  MMT_MACHINE_TRANSLATION: z.string(),
  ['ONSITE-INTERPRETING']: z.string(),
  PREPARING: z.string(),
  ['REMOTE-INTERPRETING']: z.string(),
  ['REVISIONE~second_pass']: z.string(),
  ['REVISIONE~second_pass_external']: z.string(),
  RLHF_ANNOTATION: z.string(),
  RLHF_ANNOTATION_REVISION: z.string(),
  RLHF_FLAGS_ANNOTATION: z.string(),
  SEO: z.string(),
  SFT_ANNOTATION: z.string(),
  SFT_ANNOTATION_REVISION: z.string(),
  ['TRADUZIONE~adaptation']: z.string(),
})

/**
 * The job-type vocabulary: the values `jobs.JOB_TYPE` can actually hold.
 *
 * Deliberately NOT the same thing as the keyspace of `job-types.json`. That file
 * additionally publishes four `<TYPE>~<subtype>` compounds, which are lookup keys
 * for a more specific label — never values a job can carry. `REVISIONE~second_pass`
 * is a job whose `jobType` is `REVISIONE` and whose `subType` is `second_pass`.
 *
 * Anything that produces a `jobType` must draw from here, not from
 * `JobTypesResponseSchema.keyof()`, or it invents job types that cannot exist.
 */
export const JOB_TYPE_VALUES = JobTypesResponseSchema.keyof().options.filter(
  (key) => !key.includes('~'),
)

const JobOptionsSchema = z.object({
  showSourceLanguage: z.boolean(),
  showTargetLanguage: z.boolean(),
})

export const JobTypesOptionsResponseSchema = z.object({
  ASSEVERAZIONE: JobOptionsSchema,
  BOLLI: JobOptionsSchema,
  CERTIFICATION: JobOptionsSchema,
  COPYREVIEW: JobOptionsSchema,
  ['CREDIT NOTE']: JobOptionsSchema,
  DISCOUNT: JobOptionsSchema,
  DTP: JobOptionsSchema,
  EMBEDDING: JobOptionsSchema,
  POSTEDITING: JobOptionsSchema,
  REVISIONE: JobOptionsSchema,
  ['SOUND-EDITING']: JobOptionsSchema,
  SPEDIZIONE: JobOptionsSchema,
  SUBTITLING: JobOptionsSchema,
  TRADUZIONE: JobOptionsSchema,
  TRANSCR: JobOptionsSchema,
  TRANSCREATION: JobOptionsSchema,
  ['video-bundle']: JobOptionsSchema,
  ['VIDEO-EDITING']: JobOptionsSchema,
  VOICEOVER: JobOptionsSchema,
  SUBTITLING_REVISION: JobOptionsSchema,
  VOICEOVER_REVISION: JobOptionsSchema,
  TRANSCREATION_REVISION: JobOptionsSchema,
  FINANZIARIO: JobOptionsSchema,
  FORNITORE: JobOptionsSchema,
  ['mymemory-plugin-pro']: JobOptionsSchema,
  ['SPESE BANC.']: JobOptionsSchema,
  ['STAMP DUTY']: JobOptionsSchema,
  UNKNOWN: JobOptionsSchema,
  VALUTAZIONE: JobOptionsSchema,
  ['AI-LABELING']: JobOptionsSchema,
  QA: JobOptionsSchema,
  ['SPECIAL DISCOUNT']: JobOptionsSchema,
  ['QUALITY DISCOUNT']: JobOptionsSchema,
  ['DELAY DISCOUNT']: JobOptionsSchema,
  ['GOODWILL DISCOUNT']: JobOptionsSchema,
  DUBBING: JobOptionsSchema,
  LQA: JobOptionsSchema,
  ['SWORN_TRANSLATION']: JobOptionsSchema,
  ['AUDIO_TRANSCRPT']: JobOptionsSchema,
  ['REVISIONE~second_pass']: JobOptionsSchema,
  ['TRADUZIONE~adaptation']: JobOptionsSchema,
  AUDIO_ANNOTATION: JobOptionsSchema,
  AUDIO_ANNOTATION_REVISION: JobOptionsSchema,
  ['BUG-FIXING']: JobOptionsSchema,
  ['CLEAN-UP']: JobOptionsSchema,
  CONSULTANCY: JobOptionsSchema,
  EXTRA_SERVICES: JobOptionsSchema,
  LL_SECOND_PASS: JobOptionsSchema,
  LSO: JobOptionsSchema,
  MACHINE_TRANSLATION: JobOptionsSchema,
  ['MACHINE_TRANSLATION~lara']: JobOptionsSchema,
  MMT_MACHINE_TRANSLATION: JobOptionsSchema,
  ['ONSITE-INTERPRETING']: JobOptionsSchema,
  PREPARING: JobOptionsSchema,
  ['REMOTE-INTERPRETING']: JobOptionsSchema,
  ['REVISIONE~second_pass_external']: JobOptionsSchema,
  RLHF_ANNOTATION: JobOptionsSchema,
  RLHF_ANNOTATION_REVISION: JobOptionsSchema,
  RLHF_FLAGS_ANNOTATION: JobOptionsSchema,
  SEO: JobOptionsSchema,
  SFT_ANNOTATION: JobOptionsSchema,
  SFT_ANNOTATION_REVISION: JobOptionsSchema,
})

export const CustomerResponseSchema = z.object({
  customerType: z.string(),
})
export type CustomerResponseSchema = z.infer<typeof CustomerResponseSchema>
